import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { showError } from "@/lib/errors";
import { toast } from "sonner";
import {
  Wand2, RefreshCw, Loader2, CheckCircle2, ArrowRight, XCircle,
  ShieldAlert, AlertTriangle, Users, Building2, Crown, Sliders,
} from "lucide-react";

const KIND_META = {
  coach:      { label: "Coach",      Icon: Users,     route: "/coachs" },
  club:       { label: "Club",       Icon: Building2, route: "/clubs" },
  federation: { label: "Fédération", Icon: Crown,     route: "/federations" },
};

const ACTION_HINT_META = {
  auto_link:      { label: "Match fort",    variant: "default",      Icon: CheckCircle2, helper: "Score ≥ 90 et écart marqué avec le 2ᵉ candidat → application sûre." },
  review:         { label: "À vérifier",    variant: "secondary",    Icon: AlertTriangle, helper: "Plusieurs candidats proches, à arbitrer manuellement." },
  no_match:       { label: "Aucun match",   variant: "outline",      Icon: XCircle,      helper: "Aucun candidat ne dépasse le seuil — créer la fiche ou ignorer." },
  already_linked: { label: "Déjà lié",      variant: "outline",      Icon: ShieldAlert,  helper: "Un EntityLink actif existe déjà pour ce couple." },
};

function ScoreChip({ score }) {
  const tone =
    score >= 90 ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"
    : score >= 75 ? "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30"
    : "bg-muted text-muted-foreground border-border";
  return (
    <span className={`text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded border ${tone}`}>
      {score}
    </span>
  );
}

function ProposalCard({ proposal, selected, onSelectChange, onApply, onDismiss }) {
  const [busy, setBusy] = useState(false);
  const [chosenTargetId, setChosenTargetId] = useState(proposal.matches?.[0]?.id || null);
  const kindMeta = KIND_META[proposal.kind] || KIND_META.club;
  const hintMeta = ACTION_HINT_META[proposal.action_hint] || ACTION_HINT_META.review;
  const HintIcon = hintMeta.Icon;
  const KindIcon = kindMeta.Icon;

  const chosen = proposal.matches?.find((m) => m.id === chosenTargetId);

  const apply = async () => {
    if (!chosenTargetId) return;
    setBusy(true);
    try {
      await onApply([{
        athlete_id: proposal.athlete.id,
        kind: proposal.kind,
        legacy_string: proposal.legacy_string,
        target_id: chosenTargetId,
      }]);
    } finally { setBusy(false); }
  };

  const dismiss = async () => {
    setBusy(true);
    try { await onDismiss(proposal); }
    finally { setBusy(false); }
  };

  return (
    <Card
      className="p-3.5 space-y-2.5"
      data-testid={`proposal-card-${proposal.athlete.id}-${proposal.kind}-${proposal.legacy_string.replace(/\s+/g, "_").slice(0, 20)}`}
    >
      <div className="flex items-start gap-3">
        {proposal.action_hint !== "already_linked" && (
          <Checkbox
            data-testid="proposal-select"
            checked={!!selected}
            onCheckedChange={(v) => onSelectChange(!!v)}
            disabled={proposal.matches.length === 0}
            className="mt-1"
          />
        )}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/athletes/${proposal.athlete.slug || proposal.athlete.id}`} className="text-sm font-semibold hover:underline">
              {proposal.athlete.name || "Athlète"}
            </Link>
            <span className="text-xs text-muted-foreground">·</span>
            <Badge variant="outline" className="text-[10px] gap-1">
              <KindIcon className="h-3 w-3" />{kindMeta.label}
            </Badge>
            <Badge variant={hintMeta.variant} className="text-[10px] gap-1">
              <HintIcon className="h-3 w-3" />{hintMeta.label}
            </Badge>
            {proposal.best_score > 0 && <ScoreChip score={proposal.best_score} />}
          </div>

          {/* Legacy -> matches */}
          <div className="mt-2 flex items-center gap-2 text-sm">
            <span className="px-2 py-0.5 rounded bg-muted font-mono text-xs">{proposal.legacy_string}</span>
            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            {proposal.matches.length === 0 ? (
              <span className="text-xs text-muted-foreground italic">aucune correspondance dans l'annuaire</span>
            ) : (
              <div className="flex items-center gap-1.5 flex-wrap">
                {proposal.matches.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setChosenTargetId(m.id)}
                    data-testid={`proposal-match-${m.id}`}
                    className={`text-xs px-2 py-1 rounded border flex items-center gap-1.5 transition-colors ${
                      chosenTargetId === m.id ? "bg-[hsl(var(--aito-lagoon))] text-white border-transparent" : "hover:bg-accent"
                    }`}
                  >
                    <span className="truncate max-w-[200px]">{m.name}</span>
                    <ScoreChip score={m.score} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {proposal.existing_link_id && (
            <p className="text-[11px] text-muted-foreground italic mt-1">
              Un lien actif existe déjà (id : <code>{proposal.existing_link_id.slice(0, 8)}…</code>). Aucune action requise.
            </p>
          )}
        </div>

        {/* Quick actions */}
        {proposal.action_hint !== "already_linked" && (
          <div className="flex flex-col gap-1.5 items-stretch shrink-0">
            <Button
              size="sm"
              variant="default"
              onClick={apply}
              disabled={busy || !chosenTargetId}
              data-testid="proposal-apply"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
              Lier
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={dismiss}
              disabled={busy}
              data-testid="proposal-dismiss"
            >
              <XCircle className="h-3.5 w-3.5 mr-1" />Ignorer
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

export default function AdminLegacyMigrationPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [stats, setStats] = useState(null);
  const [minScore, setMinScore] = useState(60);
  const [tab, setTab] = useState("auto_link");
  const [selected, setSelected] = useState({}); // proposalKey -> bool
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/connexion?next=/admin/legacy-migration"); return; }
    const roles = user.roles || [];
    if (!roles.includes("admin") && !roles.includes("copf")) {
      toast.error("Accès réservé aux administrateurs.");
      navigate("/dashboard");
    }
  }, [authLoading, user, navigate]);

  const scan = async () => {
    setLoading(true);
    setSelected({});
    try {
      const { data } = await api.get(`/admin/legacy-migration/scan?min_score=${minScore}`);
      setProposals(data?.proposals || []);
      setStats(data?.stats || null);
      toast.success(`Scan terminé : ${data?.proposals?.length || 0} propositions.`);
    } catch (e) {
      showError(e, { fallback: "Échec du scan" });
    } finally { setLoading(false); }
  };

  const proposalKey = (p) => `${p.athlete.id}::${p.kind}::${p.legacy_string}`;

  const filtered = useMemo(() => {
    if (tab === "all") return proposals;
    return proposals.filter((p) => p.action_hint === tab);
  }, [proposals, tab]);

  const selectedCount = useMemo(() => Object.values(selected).filter(Boolean).length, [selected]);

  const allVisibleSelected = filtered.length > 0 && filtered.every((p) => p.action_hint === "already_linked" || selected[proposalKey(p)]);

  const toggleAllVisible = (value) => {
    setSelected((prev) => {
      const next = { ...prev };
      filtered.forEach((p) => {
        if (p.action_hint === "already_linked" || p.matches.length === 0) return;
        next[proposalKey(p)] = value;
      });
      return next;
    });
  };

  const applyOne = async (items) => {
    try {
      const { data } = await api.post("/admin/legacy-migration/apply", { proposals: items, remove_legacy: true });
      toast.success(`${data.created} lien(s) créé(s), ${data.removed_strings} chaîne(s) nettoyée(s).`);
      // Retirer du listing
      setProposals((prev) => prev.filter((p) => !items.some((it) => it.athlete_id === p.athlete.id && it.kind === p.kind && it.legacy_string === p.legacy_string)));
    } catch (e) {
      showError(e, { fallback: "Échec de l'application" });
    }
  };

  const dismissOne = async (p) => {
    try {
      await api.post("/admin/legacy-migration/dismiss", {
        athlete_id: p.athlete.id, kind: p.kind, legacy_string: p.legacy_string,
      });
      toast.success("Chaîne ignorée — n'apparaîtra plus.");
      setProposals((prev) => prev.filter((x) => x !== p));
    } catch (e) {
      showError(e, { fallback: "Échec" });
    }
  };

  const applyBulk = async () => {
    const items = proposals
      .filter((p) => selected[proposalKey(p)] && p.matches.length > 0)
      .map((p) => ({
        athlete_id: p.athlete.id,
        kind: p.kind,
        legacy_string: p.legacy_string,
        target_id: p.matches[0].id, // best match (par défaut)
      }));
    if (items.length === 0) { toast.error("Aucune proposition sélectionnée."); return; }
    setApplying(true);
    try {
      await applyOne(items);
      setSelected({});
    } finally { setApplying(false); }
  };

  if (!user) return null;

  return (
    <div className="container max-w-6xl mx-auto py-6 px-4 space-y-5" data-testid="admin-legacy-migration-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-display font-bold flex items-center gap-2">
            <Wand2 className="h-6 w-6 text-[hsl(var(--aito-lagoon))]" />
            Réconciliation des liens legacy
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            Compare les coachs, clubs et fédérations cités en texte libre dans les fiches athlètes avec l'annuaire officiel.
            Les liens créés sont marqués <code>declared</code> (en attente de validation par l'entité cible). La chaîne legacy est retirée de la fiche après application.
          </p>
        </div>
      </div>

      {/* Controls */}
      <Card className="p-4 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Sliders className="h-4 w-4 text-muted-foreground" />
            <label className="text-xs text-muted-foreground" htmlFor="min-score">Score min :</label>
            <Input
              id="min-score"
              data-testid="min-score-input"
              type="number"
              min={0}
              max={100}
              value={minScore}
              onChange={(e) => setMinScore(parseInt(e.target.value || "0", 10))}
              className="w-20"
            />
          </div>
          <Button onClick={scan} disabled={loading} data-testid="scan-btn">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
            Lancer le scan
          </Button>
          {selectedCount > 0 && (
            <Button variant="default" onClick={applyBulk} disabled={applying} data-testid="apply-bulk-btn">
              {applying ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />}
              Lier {selectedCount} sélection(s)
            </Button>
          )}
        </div>

        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 pt-2 text-center">
            {[
              ["athletes_scanned", "Athlètes"],
              ["strings_total",   "Chaînes"],
              ["matched",         "Auto-link"],
              ["ambiguous",       "Ambigus"],
              ["no_match",        "Aucun match"],
              ["already_linked",  "Déjà liés"],
              ["dismissed",       "Ignorés"],
            ].map(([k, lbl]) => (
              <div key={k} className="rounded-lg border bg-muted/30 p-2">
                <div className="text-lg font-display font-bold tabular-nums">{stats[k] ?? 0}</div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{lbl}</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Tabs by action_hint */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="proposals-tabs">
          {[
            ["auto_link",      `Match fort (${proposals.filter((p) => p.action_hint === "auto_link").length})`],
            ["review",         `À vérifier (${proposals.filter((p) => p.action_hint === "review").length})`],
            ["no_match",       `Aucun match (${proposals.filter((p) => p.action_hint === "no_match").length})`],
            ["already_linked", `Déjà liés (${proposals.filter((p) => p.action_hint === "already_linked").length})`],
            ["all",            `Tout (${proposals.length})`],
          ].map(([k, lbl]) => (
            <TabsTrigger key={k} value={k} data-testid={`proposals-tab-${k}`}>{lbl}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={tab} className="mt-3 space-y-2">
          {filtered.length > 0 && (
            <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground px-1">
              <label className="flex items-center gap-2">
                <Checkbox
                  data-testid="proposals-select-all"
                  checked={allVisibleSelected}
                  onCheckedChange={(v) => toggleAllVisible(!!v)}
                />
                Tout sélectionner ({filtered.length})
              </label>
            </div>
          )}

          {loading ? (
            <>
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </>
          ) : filtered.length === 0 ? (
            <Card className="p-8 text-center" data-testid="proposals-empty">
              {stats ? (
                <p className="text-sm text-muted-foreground">Aucune proposition dans cette catégorie.</p>
              ) : (
                <p className="text-sm text-muted-foreground">Lance un premier scan pour générer les propositions.</p>
              )}
            </Card>
          ) : (
            filtered.map((p) => (
              <ProposalCard
                key={proposalKey(p)}
                proposal={p}
                selected={!!selected[proposalKey(p)]}
                onSelectChange={(v) => setSelected((prev) => ({ ...prev, [proposalKey(p)]: v }))}
                onApply={applyOne}
                onDismiss={dismissOne}
              />
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
